import { useTranslation } from 'react-i18next';
import {
  KoobAlert,
  KoobTooltip,
  KoobBadge,
  KoobButton,
  KoobInputStepper,
  sanitizeRichText,
} from '@koob/ui';
import { useAsync } from 'react-async';
import { getExperience } from '../../../api/experiences';
import { formatCurrency } from '../../../utils';
import KoobPlaceholder from '../../../components/Koob/KoobPlaceholder';
import React, { useState, useEffect, useRef } from 'react';
import ExperienceExtrasModal from './ExperienceExtrasModal';
import ExperienceExtraComposition from './ExperienceExtraComposition';
import { formatExtraPrice } from '../utils';
import {
  ExtraRequirementEnum,
  ExtraScopeEnum,
  ExtraSourceEnum,
} from '@koob/enums';
import {
  getExtraOccurrencePax,
  getHalvedOccurrencePax,
  getImageUrl,
  getPricingForCompositionExtra,
  isExtraSplittable,
} from '@koob/utils';
import { useLanguage, useSnack } from 'hooks';
import { useTripFilters } from '@koob/trip-designer';
import { v4 as uuidv4 } from 'uuid';

const EXTRA_COMPATIBILITY_COLOR = {
  bestMatch: 'green',
  neutral: 'blue',
  incompatible: 'red',
};

export default function ExperienceExtrasModalListItem({
  extraId,
  extraName,
  extraType,
  extraScope,
  compatibility,
  parentExtra,
  selectedExtras,
  setSelectedExtras,
  updateExtrasForDay,
  dayIndex,
  programDayIndex,
  prices,
  currency,
  experience,
  experienceComposition,
  parentOccurrences,
  source,
  disabled,
  readOnly = false,
  recommendationMode = false,
  transitionalExperienceId,
  parentTargetDayIndex,
  serviceDayIndex,
  availabilitySearched = true,
}) {
  const { t } = useTranslation('experiences');
  const { notify } = useSnack();

  const [showExtrasModal, setShowExtrasModal] = useState(false);
  // Narrowed to the lines THIS card owns: on a day carrying the same service
  // both as its own option and as a sub-option of another one, matching on `id`
  // alone made each card inherit the other's flag.
  const [skipChildrenExtra, setSkipChildrenExtra] = useState(
    (Array.isArray(selectedExtras) ? selectedExtras : []).find(
      extra =>
        extra.id === extraId &&
        (extra.parentExtraId ?? null) === (parentExtra?.id ?? null),
    )?.skipChildrenExtra ?? false,
  );
  const { lang } = useLanguage();
  const tripFilters = useTripFilters();
  const locale =
    tripFilters?.locale ?? tripFilters?.clientFolder?.locale ?? lang;

  const { data, isLoading } = useAsync({
    promiseFn: getExperience,
    experienceId: extraId,
    locale,
  });
  const extra = data?.data;

  // In recommendation mode, determine the scope (global/day) based on
  // where the extra is configured in the current experience (A)
  // Check day extras FIRST since they're more specific than global extras
  const getExtraConfiguration = () => {
    if (!experience) return null;

    // Check if extra is in any program day's suggested experiences FIRST
    // Day configuration takes precedence over global
    const programs = experience?.programs ?? [];
    for (let i = 0; i < programs.length; i++) {
      const dayExtra = programs[i]?.suggestedExperiences?.find(
        ex => ex.id === extraId,
      );
      if (dayExtra) {
        return {
          extra: dayExtra,
          scope: ExtraScopeEnum.DAY,
          programDayIndex: i,
        };
      }
    }

    // Check if extra is in global extras
    const globalExtra = experience?.extras?.find(ex => ex.id === extraId);
    if (globalExtra) {
      return {
        extra: globalExtra,
        scope: ExtraScopeEnum.GLOBAL,
        programDayIndex: null,
      };
    }

    // Not found in current experience - will show error
    return null;
  };

  const recommendedExtraConfig = recommendationMode
    ? getExtraConfiguration()
    : null;

  // Determine effective scope - in recommendation mode, use the detected scope
  // from where the extra is configured in the current experience
  const effectiveScope = recommendedExtraConfig?.scope ?? extraScope;
  const effectiveProgramDayIndex =
    recommendedExtraConfig?.programDayIndex ?? programDayIndex ?? dayIndex;

  // Compute this extra's target day index for passing to children modals
  const computedTargetDayIndex = (() => {
    const experienceFirstDayIndex = dayIndex - (programDayIndex ?? 0);
    if (effectiveScope === ExtraScopeEnum.GLOBAL)
      return experienceFirstDayIndex;
    if (transitionalExperienceId)
      return experienceFirstDayIndex + (effectiveProgramDayIndex ?? 0);
    return dayIndex;
  })();

  const experienceExtra =
    effectiveScope === ExtraScopeEnum.GLOBAL
      ? experience?.extras?.find(ex => ex.id === extraId)
      : experience?.programs?.map(program =>
          program.suggestedExperiences?.find(ex => ex.id === extraId),
        )?.[effectiveProgramDayIndex];

  // Secondary services stop at one level. `experienceExtra` is looked up by
  // `extraId` alone, so a sub-option also configured as a top-level service of
  // the experience would otherwise inherit that service's own sub-options and
  // open a nested modal on selection.
  const childrenExtras = parentExtra
    ? []
    : [
        ...(experienceExtra?.required?.map(ex => ({
          ...ex,
          type: ExtraRequirementEnum.REQUIRED,
        })) ?? []),
        ...(experienceExtra?.suggested?.map(ex => ({
          ...ex,
          type: ExtraRequirementEnum.SUGGESTED,
        })) ?? []),
      ];

  const safeSelectedExtras = Array.isArray(selectedExtras)
    ? selectedExtras
    : [];

  // For transition children, resolve the effective day index from the parent
  const resolvedParentDayIndex = (() => {
    if (!parentExtra) return null;
    const storedParent = safeSelectedExtras.find(
      ex => ex.id === parentExtra.id && ex.experienceId === experience?.id,
    );
    if (storedParent?.transitionalExperienceId) {
      return storedParent.dayIndex;
    }
    if (parentExtra.transitionalExperienceId && parentTargetDayIndex != null) {
      return parentTargetDayIndex;
    }
    return null;
  })();

  // Same one-level rule: a sub-option must not price in the sub-options of a
  // top-level service sharing its id.
  const selectedChildrenExtras = parentExtra
    ? []
    : safeSelectedExtras.filter(extra => extra.parentExtraId === extraId);
  // KD-3650 — the identity tuple below no longer designates a single line but
  // the whole set of occurrences of the service, told apart by `occurrenceKey`.
  // For global scope, don't compare dayIndex since extras can be on any day of
  // the experience. For day scope, require matching dayIndex.
  const matchesThisService = extra => {
    const baseMatch =
      extra.id === extraId &&
      extra.experienceId === experience?.id &&
      extra.scope === effectiveScope &&
      (extra?.parentExtraId ?? null) === (parentExtra?.id ?? null);

    if (effectiveScope === ExtraScopeEnum.GLOBAL) {
      return baseMatch;
    }
    const effectiveDay =
      resolvedParentDayIndex ??
      (transitionalExperienceId ? computedTargetDayIndex : dayIndex);

    return baseMatch && extra?.dayIndex === effectiveDay;
  };

  const sortOccurrences = list =>
    [...list].sort((a, b) => (a?.rank ?? 0) - (b?.rank ?? 0));

  const selectedOccurrences = sortOccurrences(
    safeSelectedExtras.filter(matchesThisService),
  );

  const selectedExtra = selectedOccurrences[0];

  // The day every occurrence of this service is filed on: a global service
  // lives on the first day of the experience instance, a secondary one follows
  // its parent, a transition one follows its program day.
  const targetDayIndex = (() => {
    const experienceFirstDayIndex = dayIndex - (programDayIndex ?? 0);

    if (effectiveScope === ExtraScopeEnum.GLOBAL) {
      return experienceFirstDayIndex;
    }
    if (resolvedParentDayIndex != null) {
      return resolvedParentDayIndex;
    }
    if (transitionalExperienceId) {
      return experienceFirstDayIndex + (effectiveProgramDayIndex ?? 0);
    }
    return dayIndex;
  })();

  const maxAdults = experienceComposition?.adults ?? 0;
  const tripChildrenBirthdates =
    experienceComposition?.childrenBirthdates ?? [];
  const maxChildren = tripChildrenBirthdates.length;
  const tripPax = maxAdults + maxChildren;

  const buildOccurrence = ({
    occurrenceKey,
    rank,
    adults,
    childrenBirthdates,
  }) => ({
    occurrenceKey,
    rank,
    adults,
    adultCount: adults,
    children: childrenBirthdates.length,
    childrenBirthdates: [...childrenBirthdates],
    count: adults + childrenBirthdates.length,
  });

  // Children are drawn from the trip pool in rank order, so the same child is
  // never counted in two occurrences at once.
  const assignChildrenBirthdates = list => {
    let offset = 0;
    return sortOccurrences(list).map(occurrence => {
      const childrenCount =
        occurrence.children ?? occurrence.childrenBirthdates?.length ?? 0;
      const childrenBirthdates = tripChildrenBirthdates.slice(
        offset,
        offset + childrenCount,
      );
      offset += childrenBirthdates.length;
      return buildOccurrence({ ...occurrence, childrenBirthdates });
    });
  };

  // Lines saved before KD-3650 carry no key: one is minted on read so every
  // occurrence stays addressable, without rewriting what is already stored.
  const [occurrences, setOccurrences] = useState(() =>
    (selectedOccurrences.length ? selectedOccurrences : [null]).map(
      (occurrence, index) =>
        buildOccurrence({
          occurrenceKey: occurrence?.occurrenceKey ?? uuidv4(),
          rank: occurrence?.rank ?? index,
          // The stored party is read as a WHOLE, exactly as the timeline chips
          // read it. Falling back field by field topped an occurrence booked
          // for adults only — neither a booking nor a saved trip records an
          // empty `childrenBirthdates` — up with every child of the trip, and
          // on each occurrence at once: the modal then announced more
          // travellers than the chip it was opened from, and re-filing the
          // line wrote that invented party back.
          ...getExtraOccurrencePax(occurrence, experienceComposition),
        }),
    ),
  );

  // KD-3650 — a sub-option is carried by the service it hangs from, so it
  // follows its split: one occurrence per parent occurrence, each with that
  // occurrence's own party. It never owns a split of its own — the parent's is
  // the only one there is, which is why nothing here is editable.
  //
  // A parent that hands down no occurrence list at all (an older call site) is
  // read as a single occurrence, exactly as before.
  const parentOccurrenceList = parentOccurrences?.length
    ? parentOccurrences
    : [experienceComposition];

  // The key of a line already ticked under a parent occurrence is kept, so the
  // selection survives a re-render and a reload. A parent occurrence nothing is
  // ticked under yet gets one minted ONCE and held in a ref: minting it in the
  // render body would hand React a new key on every pass, and `addExtra` would
  // file a new line each time.
  const mintedOccurrenceKeys = useRef(new Map());

  const occurrenceKeyUnder = parentOccurrenceKey => {
    const stored = selectedOccurrences.find(
      line => line.parentOccurrenceKey === parentOccurrenceKey,
    )?.occurrenceKey;
    if (stored) return stored;

    if (!mintedOccurrenceKeys.current.has(parentOccurrenceKey)) {
      mintedOccurrenceKeys.current.set(parentOccurrenceKey, uuidv4());
    }
    return mintedOccurrenceKeys.current.get(parentOccurrenceKey);
  };

  const effectiveOccurrences = parentExtra
    ? parentOccurrenceList.map((parentOccurrence, index) => ({
        ...buildOccurrence({
          occurrenceKey: occurrenceKeyUnder(parentOccurrence?.occurrenceKey),
          rank: parentOccurrence?.rank ?? index,
          adults: parentOccurrence?.adults ?? 0,
          childrenBirthdates: parentOccurrence?.childrenBirthdates ?? [],
        }),
        parentOccurrenceKey: parentOccurrence?.occurrenceKey,
      }))
    : occurrences;

  const setPax = (occurrenceKey, { adults, children }) => {
    setOccurrences(current => {
      const otherPax = current
        .filter(occurrence => occurrence.occurrenceKey !== occurrenceKey)
        .reduce(
          (acc, occurrence) =>
            acc + occurrence.adults + occurrence.childrenBirthdates.length,
          0,
        );
      // AC7 — an occurrence can never take travellers already assigned to
      // another one.
      const available = Math.max(tripPax - otherPax, 0);

      return assignChildrenBirthdates(
        current.map(occurrence => {
          if (occurrence.occurrenceKey !== occurrenceKey) return occurrence;

          const nextAdults = Math.min(
            Math.max(adults ?? occurrence.adults, 0),
            Math.min(maxAdults, available),
          );
          const nextChildren = Math.min(
            Math.max(children ?? occurrence.childrenBirthdates.length, 0),
            Math.min(maxChildren, Math.max(available - nextAdults, 0)),
          );
          // Never let a service end up with zero travellers.
          if (nextAdults + nextChildren < 1) return occurrence;

          return { ...occurrence, adults: nextAdults, children: nextChildren };
        }),
      );
    });
  };

  // FR-2 — the new occurrence is served by the travellers nobody uses yet
  // (adults and children alike); failing that, the biggest occurrence is
  // halved across both. No traveller is created.
  const addOccurrence = () => {
    setOccurrences(current => {
      const assignedAdults = current.reduce(
        (acc, occurrence) => acc + occurrence.adults,
        0,
      );
      const assignedChildren = current.reduce(
        (acc, occurrence) => acc + occurrence.childrenBirthdates.length,
        0,
      );

      let updated = current;
      let newAdults = Math.max(maxAdults - assignedAdults, 0);
      let newChildren = Math.max(maxChildren - assignedChildren, 0);

      if (newAdults + newChildren < 1) {
        const biggest = current.reduce(
          (acc, occurrence) =>
            occurrence.adults + occurrence.childrenBirthdates.length >
            acc.adults + acc.childrenBirthdates.length
              ? occurrence
              : acc,
          current[0],
        );
        const { adults: movedAdults, children: movedChildren } =
          getHalvedOccurrencePax({
            adults: biggest?.adults ?? 0,
            children: biggest?.childrenBirthdates.length ?? 0,
          });
        if (movedAdults + movedChildren < 1) return current;

        updated = current.map(occurrence =>
          occurrence.occurrenceKey === biggest.occurrenceKey
            ? {
                ...occurrence,
                adults: occurrence.adults - movedAdults,
                children: occurrence.childrenBirthdates.length - movedChildren,
              }
            : occurrence,
        );
        newAdults = movedAdults;
        newChildren = movedChildren;
      }

      const nextRank =
        current.reduce(
          (acc, occurrence) => Math.max(acc, occurrence.rank ?? 0),
          0,
        ) + 1;

      return assignChildrenBirthdates([
        ...updated,
        {
          occurrenceKey: uuidv4(),
          rank: nextRank,
          adults: newAdults,
          children: newChildren,
          childrenBirthdates: [],
        },
      ]);
    });
  };

  const applicableExtraPrice = prices?.find(price => price.id === extraId);

  // The card loads the service on its own, and that request can fail while the
  // service is still listed and priced: the name is then read from the list it
  // was offered in, or from its price entry, rather than left blank.
  const displayName = extra?.name || extraName || applicableExtraPrice?.name;
  const applicablePrices = applicableExtraPrice?.pricings;
  const minPax = applicableExtraPrice?.minPax ?? extra?.minPax ?? null;

  // A secondary service added before KD-3650 carries no parent occurrence: it
  // stays attached to the first one rather than being counted n times.
  const childrenExtrasOf = (occurrenceKey, isPrimary) =>
    selectedChildrenExtras.filter(child =>
      child?.parentOccurrenceKey
        ? child.parentOccurrenceKey === occurrenceKey
        : isPrimary,
    );

  const sumChildren = (children, key) =>
    children.reduce((acc, childExtra) => {
      const childPax =
        (childExtra?.adults ?? childExtra?.adultCount ?? 0) +
        (childExtra?.childrenBirthdates?.length ?? 0);

      return (
        acc + (childExtra?.[key] ?? 0) * (childExtra.perPax ? childPax : 1)
      );
    }, 0);

  const pricedOccurrences = effectiveOccurrences.map((occurrence, index) => {
    const priceObj = formatExtraPrice(
      occurrence,
      getPricingForCompositionExtra({
        composition: occurrence,
        pricings: applicablePrices,
      }),
    );

    const ownChildrenExtras = childrenExtrasOf(
      occurrence.occurrenceKey,
      index === 0,
    );

    const paxCount = occurrence.adults + occurrence.childrenBirthdates.length;
    const multiplier = priceObj?.perPax ? paxCount : 1;

    const totalWithPromo =
      (priceObj?.price ?? 0) * multiplier +
      sumChildren(ownChildrenExtras, 'price');
    const totalWithoutPromo =
      (priceObj?.priceWithoutPromo ?? 0) * multiplier +
      sumChildren(ownChildrenExtras, 'priceWithoutPromo');

    return {
      ...occurrence,
      number: index + 1,
      priceObj,
      paxCount,
      totalWithPromo,
      totalWithoutPromo,
      hasPromo: totalWithoutPromo !== totalWithPromo,
    };
  });

  const primaryPriceObj = pricedOccurrences[0]?.priceObj;

  // KD-3650 — being unavailable on the period is a property of the service, not
  // of the party currently selected: it holds when the grid prices no
  // composition at all. A composition the grid does not answer — a pax count
  // under `minPax`, say — is an occurrence-level matter, already stated on its
  // own line; collapsing the whole card on it hid the steppers and left no way
  // to raise the pax back.
  const hasPrice = (applicablePrices ?? []).some(
    pricing =>
      pricing?.price !== undefined &&
      pricing?.price !== null &&
      pricing?.currency !== 'mismatch',
  );

  const serviceTotalWithPromo = pricedOccurrences.reduce(
    (acc, occurrence) => acc + occurrence.totalWithPromo,
    0,
  );

  const serviceTotalWithoutPromo = pricedOccurrences.reduce(
    (acc, occurrence) => acc + occurrence.totalWithoutPromo,
    0,
  );

  const assignedPax = pricedOccurrences.reduce(
    (acc, occurrence) => acc + occurrence.paxCount,
    0,
  );

  // AC9/AC10 — the rule lives in `@koob/utils`: both modals asked it, each with
  // its own copy of the arithmetic.
  const isSplittable = isExtraSplittable(applicablePrices);

  // FR-2 — either travellers are still unassigned, or an occurrence holds at
  // least two and can hand one over. Reading `adults` alone refused the split
  // on a party the halving handles, children included.
  const canAddOccurrence =
    assignedPax < tripPax ||
    pricedOccurrences.some(occurrence => occurrence.paxCount >= 2);

  // A sub-option mirrors the parent's occurrences, so it is numbered like them.
  // Its crosses are not offered though: an occurrence of a sub-option is not
  // removable on its own, it goes when the parent occurrence it mirrors goes.
  const showOccurrenceHeaders = pricedOccurrences.length > 1;

  // A sub-option's `experienceComposition` is only the parent's first
  // occurrence: the children row follows the whole parent split instead, so an
  // occurrence holding children still shows it when the first one holds none.
  const showChildrenStepper = parentExtra
    ? parentOccurrenceList.some(
        parentOccurrence => parentOccurrence?.childrenBirthdates?.length > 0,
      )
    : maxChildren > 0;

  if (extra && primaryPriceObj) {
    extra.perPax = !!primaryPriceObj?.perPax;
  }

  // KD-3650 — a sub-option mirrors the split of the service it hangs from, and
  // the sub-option modal is unmounted while that split is being edited: nothing
  // on the child's side can react to it. So the parent re-files its children
  // itself, in the same write as its own lines.
  //
  // Every sub-option the TO picked is laid over EVERY occurrence of the parent,
  // with that occurrence's party and a price re-resolved from the sub-option's
  // own grid — a pax change can move it into another bracket, and an occurrence
  // the grid does not answer is filed without a price, which the modal already
  // flags and holds the save back on. A line left under an occurrence that no
  // longer exists is dropped.
  const mirrorSecondaries = (list, linesDayIndex) => {
    // Only a top-level card owns a split; a sub-option has no children.
    if (parentExtra) return list;

    const isOwnSecondary = ex =>
      ex?.parentExtraId === extraId &&
      ex?.experienceId === experience?.id &&
      ex?.scope === effectiveScope &&
      ex?.dayIndex === linesDayIndex;

    const secondaries = list.filter(isOwnSecondary);
    if (!secondaries.length) return list;

    // One template per sub-service: its lines agree on everything but the
    // occurrence, so whichever comes first carries the whole choice.
    const templates = new Map();
    secondaries.forEach(ex => {
      if (!templates.has(ex.id)) templates.set(ex.id, ex);
    });

    const mirrored = [];

    templates.forEach((template, childId) => {
      const childPricings = prices?.find(
        price => price.id === childId,
      )?.pricings;

      pricedOccurrences.forEach(occurrence => {
        const composition = {
          adults: occurrence.adults,
          adultCount: occurrence.adults,
          children: occurrence.childrenBirthdates.length,
          childrenBirthdates: occurrence.childrenBirthdates,
          count: occurrence.paxCount,
        };

        const priceObj = formatExtraPrice(
          composition,
          getPricingForCompositionExtra({
            composition,
            pricings: childPricings,
          }),
        );

        mirrored.push({
          ...template,
          ...composition,
          ...priceObj,
          price: priceObj?.price ?? null,
          // The key of the line already filed under this parent occurrence is
          // kept, so a mirror pass never renames what it is only re-pricing.
          occurrenceKey:
            secondaries.find(
              ex =>
                ex.id === childId &&
                ex.parentOccurrenceKey === occurrence.occurrenceKey,
            )?.occurrenceKey ?? uuidv4(),
          rank: occurrence.rank,
          parentOccurrenceKey: occurrence.occurrenceKey,
        });
      });
    });

    return [...list.filter(ex => !isOwnSecondary(ex)), ...mirrored];
  };

  const addExtra = (
    hasCompletedChildrenExtra = false,
    overrideSelectedExtras = null,
  ) => {
    // In recommendation mode, verify the extra is configured in the current experience
    if (recommendationMode && !recommendedExtraConfig) {
      notify(t('extras.notConfiguredForExperience'), { type: 'error' });
      return;
    }

    if (
      !skipChildrenExtra &&
      !hasCompletedChildrenExtra &&
      childrenExtras.length
    ) {
      setShowExtrasModal(true);
      return;
    }

    // This component owns every occurrence of the service, so the whole set is
    // dropped and re-emitted rather than a single line (KD-3650).
    const isSameService = ex =>
      (ex?.parentExtraId ?? null) === (parentExtra?.id ?? null) &&
      ex.id === extraId &&
      ex.experienceId === experience?.id &&
      ex.scope === effectiveScope;

    const newExtras = pricedOccurrences.map(occurrence => ({
      ...extra,
      id: extraId,
      experienceId: experience?.id,
      parentExtraId: parentExtra?.id,
      // Each occurrence of a sub-option hangs from the parent occurrence it
      // mirrors, so the two splits stay paired line for line.
      parentOccurrenceKey: parentExtra
        ? (occurrence.parentOccurrenceKey ??
          experienceComposition?.occurrenceKey ??
          undefined)
        : undefined,
      dayIndex: targetDayIndex,
      adults: occurrence.adults,
      adultCount: occurrence.adults,
      children: occurrence.children,
      childrenBirthdates: occurrence.childrenBirthdates,
      count: occurrence.count,
      occurrenceKey: occurrence.occurrenceKey,
      rank: occurrence.rank,
      ...occurrence.priceObj,
      // KD-3650 — an occurrence whose pax count matches no price tier has no
      // price of its own. Stated explicitly so the spread of `extra` above can
      // never leak an unrelated amount onto the line, and so the modal can tell
      // an unsellable line apart from a free one.
      price: occurrence.priceObj?.price ?? null,
      currency: currency,
      skipChildrenExtra,
      type: extraType,
      scope: effectiveScope,
      transitionalExperienceId: transitionalExperienceId ?? null,
      // An option added by hand is never an included service, whatever its
      // price. Stated explicitly so the spread of `extra` above can never leak
      // the flag the basket uses to render "included" instead of a price, and
      // so the basket refreshes can tell it apart from what they synthesize
      // themselves from the programs' included experiences.
      isIncluded: false,
      source: ExtraSourceEnum.USER,
    }));

    // If adding to a different day than current, use updateExtrasForDay
    if (updateExtrasForDay && targetDayIndex !== dayIndex) {
      updateExtrasForDay(targetDayIndex, currentExtras =>
        mirrorSecondaries(
          [...currentExtras.filter(ex => !isSameService(ex)), ...newExtras],
          targetDayIndex,
        ),
      );
    } else {
      // Add to current day
      setSelectedExtras(
        mirrorSecondaries(
          [
            ...(overrideSelectedExtras ?? safeSelectedExtras).filter(
              ex => !(isSameService(ex) && ex.dayIndex === targetDayIndex),
            ),
            ...newExtras,
          ],
          targetDayIndex,
        ),
      );
    }

    if (parentExtra) {
      setShowExtrasModal(false);
    }
  };

  // A secondary service follows its parent's composition, a top-level one its
  // own occurrences. The parent hands down a freshly built object on every
  // render, so the effect keys on its values and never on its identity.
  const parentCompositionKey = parentExtra
    ? parentOccurrenceList
        .map(parentOccurrence =>
          [
            parentOccurrence?.occurrenceKey ?? '',
            parentOccurrence?.adults ?? 0,
            parentOccurrence?.childrenBirthdates?.length ?? 0,
          ].join('-'),
        )
        .join('|')
    : null;

  // KD-3650 — the mirror is rebuilt from the parent on every mount, and the
  // parent may well have been split since these lines were filed: the
  // sub-option modal is unmounted while the parent's own card is being edited,
  // so nothing could re-file them at the time. These two signatures say whether
  // what is stored still matches the mirror.
  const mirrorSignature = effectiveOccurrences
    .map(
      occurrence =>
        `${occurrence.occurrenceKey}:${occurrence.adults}:${occurrence.childrenBirthdates.length}`,
    )
    .join('|');

  const storedSignature = selectedOccurrences
    .map(
      line =>
        `${line.occurrenceKey}:${line.adults ?? line.adultCount ?? 0}:${line.childrenBirthdates?.length ?? 0}`,
    )
    .join('|');

  const isFirstCompositionRender = useRef(true);
  useEffect(() => {
    if (isFirstCompositionRender.current) {
      isFirstCompositionRender.current = false;
      // A mount re-files only a sub-option whose stored lines have drifted from
      // the mirror, and stays silent — as it always has — when they agree.
      if (!parentExtra || mirrorSignature === storedSignature) return;
    }
    if (!selectedOccurrences.length) return;

    addExtra(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentExtra ? parentCompositionKey : occurrences]);

  const removeExtra = () => {
    setSkipChildrenExtra(null);

    // For global extras, use the selectedExtra's dayIndex since it may be stored on a different day
    // For transition children, use the resolved parent day
    // For day extras, use the current dayIndex
    const removalDayIndex =
      effectiveScope === ExtraScopeEnum.GLOBAL && selectedExtra
        ? selectedExtra.dayIndex
        : (resolvedParentDayIndex ?? dayIndex);

    // Always use updateExtrasForDay for global extras to avoid polluting current day
    // with extras from other days (since selectedExtras may contain aggregated extras)
    if (
      updateExtrasForDay &&
      (effectiveScope === ExtraScopeEnum.GLOBAL || removalDayIndex !== dayIndex)
    ) {
      updateExtrasForDay(removalDayIndex, currentExtras =>
        currentExtras.filter(extra => {
          const isSameExperience = extra.experienceId === experience?.id;
          const isSameScope = extra.scope === effectiveScope;

          // Deleting a specific child
          if (parentExtra) {
            return !(
              isSameExperience &&
              isSameScope &&
              extra.id === extraId &&
              (extra.parentExtraId ?? null) === (parentExtra?.id ?? null)
            );
          }

          // Deleting a parent and its children
          //
          // The same service can sit on the day twice over: once as an option of its
          // own, once as a sub-option of another one. `id` names the PRODUCT, not the
          // line, so a top-level card owns only the lines that hang from nothing —
          // without that, removing the day's own option silently took down the
          // identical sub-option of a neighbouring service, which nothing on screen
          // had tied to this card.
          const isParent = extra.id === extraId && !extra.parentExtraId;
          const isChildOfParent = extra.parentExtraId === extraId;

          return !(
            isSameExperience &&
            isSameScope &&
            (isParent || isChildOfParent)
          );
        }),
      );
    } else {
      // Remove from current day
      setSelectedExtras(
        safeSelectedExtras.filter(extra => {
          const isSameExperience = extra.experienceId === experience?.id;
          const isSameDayAndScope =
            extra.dayIndex === dayIndex && extra.scope === effectiveScope;

          // Deleting a specific child
          if (parentExtra) {
            return !(
              isSameExperience &&
              isSameDayAndScope &&
              extra.id === extraId &&
              (extra.parentExtraId ?? null) === (parentExtra?.id ?? null)
            );
          }

          // Deleting a parent and its children
          //
          // The same service can sit on the day twice over: once as an option of its
          // own, once as a sub-option of another one. `id` names the PRODUCT, not the
          // line, so a top-level card owns only the lines that hang from nothing —
          // without that, removing the day's own option silently took down the
          // identical sub-option of a neighbouring service, which nothing on screen
          // had tied to this card.
          const isParent = extra.id === extraId && !extra.parentExtraId;
          const isChildOfParent = extra.parentExtraId === extraId;

          return !(
            isSameExperience &&
            isSameDayAndScope &&
            (isParent || isChildOfParent)
          );
        }),
      );
    }
  };

  // FR-4 — removing the last occurrence removes the service itself; the
  // travellers it held are not reassigned anywhere.
  const removeOccurrence = occurrenceKey => {
    if (occurrences.length <= 1) {
      removeExtra();
      return;
    }

    // The secondary services hanging from this occurrence go down with it. The
    // re-emission of the remaining occurrences is left to the effect above,
    // which runs once this state has been applied.
    const dropOwnChildren = extras =>
      extras.filter(
        ex =>
          !(
            ex.parentExtraId === extraId &&
            ex.parentOccurrenceKey === occurrenceKey
          ),
      );

    if (updateExtrasForDay && targetDayIndex !== dayIndex) {
      updateExtrasForDay(targetDayIndex, dropOwnChildren);
    } else {
      setSelectedExtras(dropOwnChildren(safeSelectedExtras));
    }

    setOccurrences(current =>
      assignChildrenBirthdates(
        current.filter(
          occurrence => occurrence.occurrenceKey !== occurrenceKey,
        ),
      ),
    );
  };

  const isChildrenExtra = !!parentExtra;

  return !experience?.state || experience?.state === 'available' ? (
    <>
      <div className="relative bg-white border shadow-k rounded-lg p-5">
        {!isLoading ? (
          <div>
            {extraType && (
              <div className="absolute -top-4 left-0 w-full flex justify-center">
                <KoobBadge
                  color={
                    extraType === ExtraRequirementEnum.REQUIRED
                      ? 'orange'
                      : 'blue'
                  }
                >
                  {extraType === ExtraRequirementEnum.REQUIRED
                    ? t('extras.required')
                    : t('extras.suggested')}
                </KoobBadge>
              </div>
            )}

            <div className="flex space-x-3">
              <img
                src={getImageUrl(extra) ?? '/img/hotel-image-placeholder.png'}
                onError={ev =>
                  (ev.target.src = '/img/hotel-image-placeholder.png')
                }
                className="w-20 h-20 rounded-lg object-cover"
                alt=""
              />

              <div className="flex w-full justify-between">
                <div className="mb-3 w-full">
                  <div
                    className="text-lg font-medium line-clamp-2 min-h-[3.5rem]"
                    title={displayName}
                  >
                    {displayName}
                  </div>

                  {[
                    extra?.durationDays,
                    extra?.durationHours,
                    extra?.durationMinutes,
                  ].some(value => value != null) && (
                    <div className="mt-1 flex gap-1.5 items-center text-gray-500 text-sm">
                      <i className="far fa-clock text-lg" />

                      <div>
                        {!(extra?.durationDays > 0) &&
                        !(extra?.durationHours > 0) &&
                        !(extra?.durationMinutes > 0)
                          ? t('durationUnitOptions.days', {
                              count: 0,
                            })
                          : null}{' '}
                        {extra?.durationDays > 0
                          ? t('durationUnitOptions.days', {
                              count: extra?.durationDays,
                            })
                          : null}{' '}
                        {extra?.durationHours > 0
                          ? t('durationUnitOptions.hours', {
                              count: extra?.durationHours,
                            })
                          : null}{' '}
                        {extra?.durationMinutes > 0
                          ? t('durationUnitOptions.minutes', {
                              count: extra?.durationMinutes,
                            })
                          : null}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  {compatibility && (
                    <KoobTooltip
                      tip={t(`extras.tooltip.${compatibility}`)}
                      color="purple"
                      hasArrow={false}
                      position="left-top"
                    >
                      <div
                        className={`text-${EXTRA_COMPATIBILITY_COLOR[compatibility]}-600`}
                      >
                        <svg
                          className="h-5 w-5"
                          height="1em"
                          viewBox="0 0 512 512"
                        >
                          <path d="M243.5 37.3c8-3.4 17-3.4 25 0l176.7 75c11.3 4.8 18.9 15.5 18.8 27.6c-.5 94-39.4 259.8-195.5 334.5c-7.9 3.8-17.2 3.8-25.1 0C87.3 399.6 48.5 233.8 48 139.8c-.1-12.1 7.5-22.8 18.8-27.6l176.7-75zM281 7.8c-16-6.8-34-6.8-50 0L54.3 82.8c-22 9.3-38.4 31-38.3 57.2c.5 99.2 41.3 280.7 213.6 363.2c16.7 8 36.1 8 52.8 0C454.7 420.7 495.5 239.2 496 140c.1-26.2-16.3-47.9-38.3-57.2L281 7.8zm82.3 195.5c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0L224 297.4l-52.7-52.7c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6l64 64c6.2 6.2 16.4 6.2 22.6 0l128-128z" />
                        </svg>
                      </div>
                    </KoobTooltip>
                  )}
                </div>
              </div>
            </div>

            <div className="w-full flex justify-between items-center transition">
              <a
                className="underline text-sm text-gray-500 hover:text-gray-600"
                href={`/experiences/${extra?.id}?locale=${locale}${
                  source ? `&source=${source}` : ''
                }`}
                target="_blank"
                rel="noreferrer"
              >
                {t('extras.learnMore')}
              </a>

              {selectedExtra && childrenExtras?.length ? (
                <div
                  className="px-3 py-1 bg-gray-500 hover:bg-gray-600 rounded-full cursor-pointer"
                  onClick={() => setShowExtrasModal(true)}
                >
                  <div className="text-sm font-medium text-white">
                    <i className="far fa-layer-plus mr-1" />
                    {t('extras.editSecondaryOptions')}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="my-2 h-20 bg-gray-100 rounded px-4 py-2 overflow-y-auto">
              <div
                className="text-sm text-gray-500 w-full truncate text-wrap"
                dangerouslySetInnerHTML={{
                  __html: sanitizeRichText(extra?.description),
                }}
              />
            </div>

            {hasPrice ? (
              <div>
                {pricedOccurrences.map(occurrence => {
                  // The `+` buttons run out as soon as every traveller of the
                  // trip is assigned to an occurrence (AC7).
                  const slack = Math.max(tripPax - assignedPax, 0);

                  return (
                    <div
                      key={occurrence.occurrenceKey}
                      className="mb-2 bg-gray-100 rounded py-2 px-4"
                    >
                      {showOccurrenceHeaders && (
                        <div className="mb-1.5 flex justify-between items-center">
                          <span className="text-xs font-medium text-gray-500">
                            {t('extras.occurrenceNumber', {
                              number: occurrence.number,
                            })}
                          </span>

                          {!readOnly && !parentExtra && (
                            <button
                              type="button"
                              className="border-none bg-transparent p-0 text-gray-400 hover:text-gray-600 cursor-pointer"
                              onClick={() =>
                                removeOccurrence(occurrence.occurrenceKey)
                              }
                              aria-label={t('extras.removeOccurrence')}
                              title={t('extras.removeOccurrence')}
                              data-testid={`remove-occurrence-${extraId}-${occurrence.occurrenceKey}`}
                            >
                              <i className="far fa-times" />
                            </button>
                          )}
                        </div>
                      )}

                      <div className="flex justify-between items-center gap-3">
                        {/* KD-3746 — a sub-option shows its parent occurrence's
                            party in the same steppers, locked: it is carried
                            over, never picked here. */}
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 w-14">
                              {t('extras.adults')}
                            </span>
                            <KoobInputStepper
                              size="sm"
                              value={occurrence.adults}
                              min={0}
                              max={
                                parentExtra
                                  ? occurrence.adults
                                  : Math.min(
                                      maxAdults,
                                      occurrence.adults + slack,
                                    )
                              }
                              onValueChange={adults =>
                                setPax(occurrence.occurrenceKey, { adults })
                              }
                              disabled={readOnly || !!parentExtra}
                              aria-label={t('extras.adults')}
                              data-testid={`adults-stepper-${extraId}-${occurrence.occurrenceKey}`}
                            />
                          </div>

                          {showChildrenStepper && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 w-14">
                                {t('extras.children')}
                              </span>
                              <KoobInputStepper
                                size="sm"
                                value={occurrence.childrenBirthdates.length}
                                min={0}
                                max={
                                  parentExtra
                                    ? occurrence.childrenBirthdates.length
                                    : Math.min(
                                        maxChildren,
                                        occurrence.childrenBirthdates.length +
                                          slack,
                                      )
                                }
                                onValueChange={children =>
                                  setPax(occurrence.occurrenceKey, {
                                    children,
                                  })
                                }
                                disabled={readOnly || !!parentExtra}
                                aria-label={t('extras.children')}
                                data-testid={`children-stepper-${extraId}-${occurrence.occurrenceKey}`}
                              />
                            </div>
                          )}
                        </div>

                        <div className="text-lg text-center font-medium text-orange-500">
                          {/* 0 is a price: a free option states its amount and
                              currency like any other. Testing the price for
                              truthiness left the free ones with an empty slot. */}
                          {occurrence.priceObj?.price != null ? (
                            <div className="text-right">
                              {occurrence.hasPromo && (
                                <div className="text-xs line-through text-gray-500">
                                  {formatCurrency({
                                    amount: occurrence.totalWithoutPromo,
                                    currency,
                                  })}
                                </div>
                              )}

                              <div className="whitespace-nowrap">
                                {formatCurrency({
                                  amount: occurrence.totalWithPromo,
                                  currency,
                                })}
                              </div>

                              {/* KD-3744 — every service states its per-traveller share, flat
                                  fees included, as the Trip Designer panel does: the amount
                                  divided by the travellers of the occurrence. */}
                              {occurrence.paxCount > 0 && (
                                <div className="mt-0.5 text-xs font-normal text-gray-500 whitespace-nowrap">
                                  {t('extras.perPax', {
                                    price: formatCurrency({
                                      amount:
                                        occurrence.totalWithPromo /
                                        occurrence.paxCount,
                                      currency,
                                    }),
                                  })}
                                </div>
                              )}
                            </div>
                          ) : (
                            /* KD-3650 — an occurrence whose pax count matches
                               no price tier cannot be sold. It says so on its
                               own line instead of leaving an empty slot, and
                               the modal holds its save back until the split is
                               fixed. */
                            <KoobTooltip
                              tip={t('extras.occurrenceNotAvailable')}
                              color="red"
                              hasArrow={false}
                              position="left-top"
                            >
                              <div
                                className="text-red-500"
                                data-testid={`occurrence-not-available-${extraId}-${occurrence.occurrenceKey}`}
                              >
                                <i className="far fa-triangle-exclamation" />
                              </div>
                            </KoobTooltip>
                          )}
                        </div>
                      </div>

                      {minPax != null && occurrence.paxCount < minPax && (
                        <div className="mt-1.5 text-xs text-orange-400">
                          {t('extras.minPaxNotReached', { count: minPax })}
                        </div>
                      )}
                    </div>
                  );
                })}

                {!parentExtra && assignedPax < tripPax && (
                  <div className="mb-2 text-xs text-gray-500">
                    {t('extras.travellersAssigned', {
                      assigned: assignedPax,
                      total: tripPax,
                    })}
                  </div>
                )}

                {pricedOccurrences.length > 1 && (
                  <div className="mb-2 flex justify-between items-center text-sm font-medium">
                    <span className="text-gray-500">{t('extras.total')}</span>

                    <div className="text-right text-orange-500">
                      {serviceTotalWithoutPromo !== serviceTotalWithPromo && (
                        <span className="mr-1.5 text-xs line-through text-gray-500">
                          {formatCurrency({
                            amount: serviceTotalWithoutPromo,
                            currency,
                          })}
                        </span>
                      )}

                      {formatCurrency({
                        amount: serviceTotalWithPromo,
                        currency,
                      })}
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center gap-3">
                  {/* Offered once the service is on the trip, and only on a
                      transfer. */}
                  {!parentExtra &&
                  !readOnly &&
                  selectedOccurrences.length > 0 &&
                  isSplittable ? (
                    <button
                      type="button"
                      className="border-none bg-transparent p-0 underline text-sm text-gray-500 hover:text-gray-600 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                      onClick={() => addOccurrence()}
                      disabled={!canAddOccurrence}
                      data-testid={`add-occurrence-${extraId}`}
                    >
                      {occurrences.length > 1
                        ? t('extras.addAnother')
                        : t('extras.splitIntoTwo')}
                    </button>
                  ) : (
                    <span />
                  )}

                  {!selectedExtra ? (
                    <KoobButton
                      variant="primary"
                      onClick={() => addExtra()}
                      disabled={
                        disabled || compatibility === 'incompatible' || readOnly
                      }
                      testId={`${
                        isChildrenExtra ? 'add-secondary' : 'add'
                      }-${extra?.name?.split(' ').join('-')}`}
                    >
                      {t('extras.add')}
                    </KoobButton>
                  ) : (
                    <KoobButton
                      onClick={() => removeExtra()}
                      variant="success"
                      disabled={readOnly}
                    >
                      {t('extras.remove')}
                    </KoobButton>
                  )}
                </div>
              </div>
            ) : !availabilitySearched ? (
              <KoobAlert
                variant="info"
                icon="fa-info-circle"
                description={t('extras.availabilityPrompt')}
              />
            ) : primaryPriceObj?.currency === 'mismatch' ? (
              <div className="w-full bg-red-100 px-4 py-2 rounded">
                <div className="text-sm text-red-400">
                  {t('extras.notAvailableCurrencyMismatch')}
                </div>
              </div>
            ) : (
              <div>
                <div className="w-full bg-orange-100 px-4 py-2 rounded">
                  <div className="text-sm text-orange-400">
                    {t('extras.notAvailableForPeriod')}
                  </div>
                </div>

                {/* KD-3749 — a service already selected is kept on display
                    in this state, and it has to stay removable. */}
                {selectedExtra && (
                  <div className="mt-3 flex justify-end">
                    <KoobButton
                      onClick={() => removeExtra()}
                      variant="success"
                      disabled={readOnly}
                    >
                      {t('extras.remove')}
                    </KoobButton>
                  </div>
                )}
              </div>
            )}

            {childrenExtras?.length ? (
              <ExperienceExtrasModal
                open={showExtrasModal}
                setOpen={setShowExtrasModal}
                experience={experience}
                experienceComposition={pricedOccurrences[0]}
                parentOccurrences={pricedOccurrences}
                parentExtra={{ ...extra, transitionalExperienceId }}
                extrasScope={effectiveScope}
                extras={childrenExtras}
                selectedExtras={selectedExtras}
                setSelectedExtras={setSelectedExtras}
                updateExtrasForDay={updateExtrasForDay}
                prices={prices}
                currency={currency}
                onContinue={(_hasChanged, updatedExtras) =>
                  addExtra(true, updatedExtras)
                }
                skipChildrenExtra={skipChildrenExtra}
                setSkipChildrenExtra={setSkipChildrenExtra}
                dayIndex={dayIndex}
                programDayIndex={programDayIndex}
                parentTargetDayIndex={computedTargetDayIndex}
                serviceDayIndex={
                  recommendationMode && !parentExtra
                    ? (recommendedExtraConfig?.programDayIndex ?? undefined)
                    : serviceDayIndex
                }
                source={source}
                readOnly={readOnly}
                availabilitySearched={availabilitySearched}
              />
            ) : null}
          </div>
        ) : (
          <div>
            <div className="flex space-x-3">
              <div>
                <KoobPlaceholder className="w-20 h-20 rounded-lg" />
              </div>

              <div className="w-full">
                <KoobPlaceholder className="w-full h-16 rounded" />
              </div>
            </div>

            <KoobPlaceholder className="mt-3 w-28 h-5 rounded" />

            <KoobPlaceholder className="my-2 w-full h-16 rounded-lg" />

            {hasPrice && (
              <>
                <KoobPlaceholder className="my-2 w-full h-6 rounded-lg" />

                <KoobPlaceholder className="w-40 h-10 rounded-lg" />
              </>
            )}
          </div>
        )}
      </div>
    </>
  ) : null;
}
